import { SortOrder } from '../../../../components/GRTable/GRTable'
import quarterService, { QuarterDataKey } from '../../../quarter/services/QuarterService'
import { GameQuarter } from '../../../quarter/types/GameQuarter'
import { MarketShareQuarter } from '../../../quarter/types/MarketShareQuarter'
import { Quarter } from '../../../quarter/types/Quarter'
import { TopGame } from '../../../top-game/types/TopGame'
import { MarketShareNumberType, MarketShareNumberTypeId } from '../../types/MarketShareNumberType'

export type TopGamesSortKey = 'revenue' | 'downloads' | 'revenue_change' | 'downloads_change'

export interface TopGamesSort {
  key: TopGamesSortKey
  order: SortOrder
}

/**
 * Returns the up-to-3 games to display in the "Top Games" column of the Quarter Overview row,
 * given the currently active sort column / direction on the table and the active number type.
 *
 * GR-2360: this preview must match the first rows of the dedicated **Top 500 Games** tab when it
 * is sorted by the same column, direction and number type. To guarantee that, the per-game ranking
 * metric is computed exactly like the Top 500 Games table (`MarketShareTopGames` /
 * `quarterService.grTableSortAccessorForTopGame*Change`):
 * - Value column   → Percentage type: *Top500Value · Value type: *Value
 * - Change column  → Percentage type: change of *Percentage · Value type: change of *Value
 *   (current quarter vs the previous quarter; 0 when no previous quarter exists)
 *
 * The candidate set is anchored on the relevant current-quarter Top 500 list (Revenue → grossingGames,
 * Downloads → freeGames) and on a positive Top 500 value, so we never surface games with no
 * current-quarter presence in the chosen list.
 *
 * The sort **direction is honoured literally** so the preview reads the same way as the Top 500 Games
 * tab: DESC surfaces the top 3 games for the metric (largest first); ASC surfaces the bottom 3 games
 * (smallest first), e.g. the biggest decliners for a *_change column.
 */
export const getMarketShareQuarterTop3Games = (
  marketShareQuarter: MarketShareQuarter,
  selectedQuarter: Quarter,
  sort: TopGamesSort,
  marketShareNumberType: MarketShareNumberType
): TopGame[] => {
  const quarterIdentifier = selectedQuarter.quarterIdentifier
  const isDownloadsSort = sort.key === 'downloads' || sort.key === 'downloads_change'
  const isChangeSort = sort.key === 'revenue_change' || sort.key === 'downloads_change'
  const isValueType = marketShareNumberType.id === MarketShareNumberTypeId.Value

  const top500AppIds = isDownloadsSort ? marketShareQuarter.freeGames : marketShareQuarter.grossingGames
  const top500AppIdSet = new Set(top500AppIds)

  // Anchor: the game must belong to the relevant current-quarter Top 500 (Revenue or Downloads).
  const top500Value = (gameQuarter: GameQuarter) => (isDownloadsSort ? gameQuarter.downloadsTop500Value : gameQuarter.revenueTop500Value)

  // Mirror the Top 500 Games tab ranking for the active column + number type so the preview and
  // the full table always agree on which games are shown and in which order.
  const sortValue = (game: TopGame) => {
    const gameQuarter = game.marketLevelQuarters[quarterIdentifier]
    if (!gameQuarter) {
      return 0
    }

    if (!isChangeSort) {
      if (isDownloadsSort) {
        return isValueType ? gameQuarter.downloadsValue : gameQuarter.downloadsTop500Value
      }
      return isValueType ? gameQuarter.revenueValue : gameQuarter.revenueTop500Value
    }

    const changeKey: QuarterDataKey = isDownloadsSort
      ? isValueType
        ? QuarterDataKey.DownloadsValue
        : QuarterDataKey.DownloadsPercentage
      : isValueType
      ? QuarterDataKey.RevenueValue
      : QuarterDataKey.RevenuePercentage

    return quarterService.getChangeDataForPreviousQuarterValue(changeKey, quarterIdentifier, game.marketLevelQuarters)
  }

  const orderCoefficient = sort.order === SortOrder.ASC ? 1 : -1

  return marketShareQuarter.games
    .filter((game) => {
      const gameQuarter = game.marketLevelQuarters[quarterIdentifier]
      return !!gameQuarter && top500AppIdSet.has(game.appId) && top500Value(gameQuarter) > 0
    })
    .sort((a, b) => {
      const valueA = sortValue(a)
      const valueB = sortValue(b)
      // Deterministic tiebreak by appId so equal-metric games keep a stable order.
      if (valueA === valueB) {
        return a.appId - b.appId
      }
      return (valueA - valueB) * orderCoefficient
    })
    .slice(0, 3)
}
